import { useEffect, useState } from "react";
import { AlertTriangle, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/button";
import { useImpersonation } from "@/contexts/ImpersonationContext";

function formatLeft(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * 代客登录提示横幅 — 在所有页面顶部显示(App.tsx 已挂)。
 *
 * 显示:正在扮演的租户名 + 剩余时间 + "退出代客"按钮。
 */
export function ImpersonationBanner() {
  const { isImpersonating, meta, stopImpersonation } = useImpersonation();
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!isImpersonating) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isImpersonating]);

  if (!isImpersonating || !meta) return null;
  const left = meta.expiresAt - now;

  return (
    <div className="bg-warning/15 border-b border-warning/40 text-warning px-4 py-2 flex items-center gap-3 sticky top-0 z-50">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <div className="flex-1 text-sm">
        <span className="font-medium">{t("impersonation.label")}</span>
        <span className="mx-2 opacity-70">·</span>
        {t("impersonation.actingAs")} <span className="font-medium">{meta.tenantName}</span>
        <span className="opacity-80 font-mono ml-1">({meta.tenantCode})</span>{" "}
        {t("impersonation.identitySuffix")}
        <span className="mx-2 opacity-70">·</span>
        <span className="font-mono">
          {t("impersonation.remaining")} {formatLeft(left)}
        </span>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => stopImpersonation()}
        className="bg-background hover:bg-warning/10"
      >
        <LogOut className="h-3.5 w-3.5 mr-1" />
        {t("impersonation.exit")}
      </Button>
    </div>
  );
}
